import {
  BottomSheet,
  Divider,
  Illustration,
  Text,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { useGetShiftBoosts } from "@src/appV2/redesign/Shift/ShiftBoost/api/useGetShiftBoosts";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { formatDistanceToNowStrict, parseISO } from "date-fns";
import pluralize from "pluralize";
import { useEffect } from "react";

interface ShiftBoostDetailsBottomSheetProps {
  modalState: UseModalState;
}

const SEEN_SHIFT_BOOST_STORAGE_KEY = "seen-shift-boost";

export function ShiftBoostDetailsBottomSheet(props: ShiftBoostDetailsBottomSheetProps) {
  const { modalState } = props;
  const worker = useDefinedWorker();
  const shiftBoostConfig = useCbhFlag(CbhFeatureFlag.SHIFT_BOOST_CONFIG, {
    defaultValue: { enabled: false },
  });
  const { data: shiftBoosts, isSuccess: getShiftBoostsSuccess } = useGetShiftBoosts(
    { workerId: worker.userId, statuses: ["AVAILABLE"] },
    { enabled: shiftBoostConfig.enabled && isDefined(worker.userId) }
  );
  const hasShiftBoosts = getShiftBoostsSuccess && shiftBoosts.data.length > 0;

  // Show the bottom sheet once per app open if the worker has a shift boost
  useEffect(() => {
    const shouldShowBottomSheet =
      hasShiftBoosts && !sessionStorage.getItem(SEEN_SHIFT_BOOST_STORAGE_KEY);
    if (shouldShowBottomSheet) {
      modalState.openModal();
      logEvent(APP_V2_USER_EVENTS.SHIFT_BOOST_BOTTOM_SHEET_OPENED, {
        availableBoosts: shiftBoosts.data.map((boost) => ({
          id: boost.id,
          amount: boost.attributes.amount,
          expiresAt: boost.attributes.expiresAt,
        })),
      });
      sessionStorage.setItem(SEEN_SHIFT_BOOST_STORAGE_KEY, shiftBoosts.data[0].id);
    }
  }, [modalState, hasShiftBoosts, shiftBoosts]);

  if (!hasShiftBoosts) {
    return null;
  }

  const soonestShiftBoost = shiftBoosts.data[0].attributes;
  const availableShiftBoosts = shiftBoosts.data.length;
  const soonestExpiration = parseISO(soonestShiftBoost.expiresAt);

  return (
    <BottomSheet modalState={modalState}>
      <Stack
        spacing={8}
        alignItems="center"
        direction="column"
        sx={{ paddingTop: 8, paddingBottom: 8 }}
      >
        <Illustration type="shift-boost" />
        <Text semibold variant="h4" align="center">
          Get a {formatDollarsAsUsd(soonestShiftBoost.amount, { maximumFractionDigits: 0 })} Shift
          Boost on your next {availableShiftBoosts > 1 ? `${availableShiftBoosts} ` : ""}
          {pluralize("booking", availableShiftBoosts)}
        </Text>
        <Stack spacing={5} alignItems="left" direction="column" width="90%">
          <Text semibold variant="body1" align="left">
            Expiration
          </Text>

          {soonestExpiration > new Date() ? (
            <Text variant="body1" align="left" color="text.secondary">
              Offer expires in {formatDistanceToNowStrict(soonestExpiration)}
            </Text>
          ) : (
            <Text variant="body1" align="left" color="text.secondary">
              Offer expired
            </Text>
          )}
          <Divider sx={{ width: "100%" }} />
          <Text semibold variant="body1" align="left">
            Eligible Bookings
          </Text>
          <Text variant="body1" align="left" color="text.secondary">
            Shift Boosts apply to open shifts only, excluding shift invites
          </Text>
        </Stack>
      </Stack>
      <DialogFooter orientation="vertical">
        <Button fullWidth size="large" variant="contained" onClick={modalState.closeModal}>
          Browse eligible shifts
        </Button>
      </DialogFooter>
    </BottomSheet>
  );
}
